import { msg, type useMessages } from 'gt-react';
import type { HarnessToolCallSummary } from '@erp/rpc';
import { NAMI_TOOL_LABELS } from '@/components/common/chat/nami/namiToolLabels';
import type {
  ToolCallEffect,
  ToolCallStatus,
} from '@/api/rpc/hooks/harness/useHarnessToolCalls';

export const TOOL_CALL_STATUS_LABEL: Record<ToolCallStatus, string> = {
  running: msg('Running', { context: 'AI assistant tool call status' }),
  ok: msg('OK', { context: 'AI assistant tool call status' }),
  failed: msg('Failed', { context: 'AI assistant tool call status' }),
  pending: msg('Awaiting apply', { context: 'AI assistant tool call status' }),
  applied: msg('Applied', { context: 'AI assistant tool call status' }),
  discarded: msg('Discarded', { context: 'AI assistant tool call status' }),
  expired: msg('Expired', { context: 'AI assistant tool call status' }),
  undone: msg('Undone', { context: 'AI assistant tool call status' }),
};

export const TOOL_CALL_EFFECT_LABEL: Record<ToolCallEffect, string> = {
  read: msg('Read', { context: 'AI assistant tool call effect' }),
  ui: msg('Page', { context: 'AI assistant tool call effect (browser UI)' }),
  direct: msg('Direct write', { context: 'AI assistant tool call effect' }),
  preview: msg('Preview', { context: 'AI assistant tool call effect' }),
  apply: msg('Apply', { context: 'AI assistant tool call effect' }),
  undo: msg('Undo', { context: 'AI assistant tool call effect' }),
};

export const TOOL_CALL_STATUSES: ToolCallStatus[] = [
  'running',
  'ok',
  'failed',
  'pending',
  'applied',
  'discarded',
  'expired',
  'undone',
];

export const TOOL_CALL_EFFECTS: ToolCallEffect[] = [
  'read',
  'ui',
  'direct',
  'preview',
  'apply',
  'undo',
];

export function toolCallStatusVariant(
  status: ToolCallStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'applied':
    case 'ok':
      return 'secondary';
    case 'failed':
      return 'destructive';
    case 'pending':
    case 'running':
      return 'default';
    default:
      return 'outline';
  }
}

export interface SummaryStat {
  key: keyof HarnessToolCallSummary['totals'];
  label: string;
  value: number;
}

const STAT_LABELS: Record<keyof HarnessToolCallSummary['totals'], string> = {
  calls: msg('Tool calls', { context: 'stat tile label' }),
  writes: msg('Writes', { context: 'stat tile label' }),
  failed: msg('Failed', { context: 'stat tile label' }),
  pending: msg('Awaiting apply', { context: 'stat tile label' }),
  users: msg('People', { context: 'stat tile label' }),
  threads: msg('Conversations', { context: 'stat tile label' }),
};

/** The stat strip, in display order, from the summary's totals. */
export function summaryStats(
  totals: HarnessToolCallSummary['totals'],
): SummaryStat[] {
  return (
    ['calls', 'writes', 'failed', 'pending', 'users', 'threads'] as const
  ).map((key) => ({ key, label: STAT_LABELS[key], value: totals[key] }));
}

/** The app path for a record the assistant acted on, or null when it has no page. */
export function entityLink(
  entityKind: string | null | undefined,
  entityId: string | null | undefined,
): string | null {
  if (!entityKind || !entityId) return null;
  switch (entityKind) {
    case 'sales-order':
      return `/sales-orders/${entityId}`;
    case 'discount-group':
      return `/discounts/${entityId}`;
    case 'customer':
      return `/customers?customerId=${entityId}`;
    default:
      return null;
  }
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
}

/** Whether an undo window is still open at `now`. */
export function undoOpen(
  undoAvailableUntil: string | null | undefined,
  now: number,
): boolean {
  return !!undoAvailableUntil && new Date(undoAvailableUntil).getTime() > now;
}

/** The human label for a tool short name, without the trailing ellipsis the progress rows use. */
export function toolLabel(
  toolName: string,
  m: ReturnType<typeof useMessages>,
): string {
  const label = (NAMI_TOOL_LABELS as Record<string, string>)[toolName];
  return label ? m(label).replace(/…$/, '') : toolName;
}
